import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useReducer,
  useRef,
} from "react";

import useReadAloudMode from "@/view/ReadAloudControls/useReadAloudMode";
import { getLanguagePreference } from "@/view/TiptapArticleBody/components/TranslateDrawer/utils/languagePreferences";
import useGetUser from "@/users/v3/useGetUser";

// Session storage key for tracking first selection in current session
const FIRST_SELECTION_SESSION_KEY = "translator-first-selection";

// State interface
interface TranslatorState {
  isTranslateMode: boolean;
  activeTranslatedItem: string | undefined;
  isFirstSelection: boolean;
  timeSinceLastSelection: number | undefined;
}

// Action types
type TranslatorAction =
  | { type: "SET_TRANSLATE_MODE"; payload: boolean }
  | { type: "SET_ACTIVE_TRANSLATED_ITEM"; payload: string | undefined }
  | { type: "SET_FIRST_SELECTION"; payload: boolean }
  | { type: "SET_TIME_SINCE_LAST_SELECTION"; payload: number | undefined }
  | { type: "RESET_TRANSLATION_STATE" }
  | {
      type: "UPDATE_LANGUAGE_SELECTION";
      payload: { language: string; isFirstSelection: boolean };
    };

// Initial state
const getInitialState = (): TranslatorState => {
  // Check session storage first to see if this is the first selection in this session
  let isFirstSelection = true;
  try {
    const stored = sessionStorage.getItem(FIRST_SELECTION_SESSION_KEY);
    if (stored === "false") {
      isFirstSelection = false;
    }
  } catch (error) {
    console.warn("Failed to read from sessionStorage:", error);
  }

  return {
    isTranslateMode: false,
    activeTranslatedItem: undefined,
    isFirstSelection,
    timeSinceLastSelection: undefined,
  };
};

// Reducer function
const translatorReducer = (
  state: TranslatorState,
  action: TranslatorAction,
): TranslatorState => {
  switch (action.type) {
    case "SET_TRANSLATE_MODE":
      return {
        ...state,
        isTranslateMode: action.payload,
      };

    case "SET_ACTIVE_TRANSLATED_ITEM":
      return {
        ...state,
        activeTranslatedItem: action.payload,
      };

    case "SET_FIRST_SELECTION":
      return {
        ...state,
        isFirstSelection: action.payload,
      };

    case "SET_TIME_SINCE_LAST_SELECTION":
      return {
        ...state,
        timeSinceLastSelection: action.payload,
      };

    case "RESET_TRANSLATION_STATE":
      return {
        ...state,
        activeTranslatedItem: undefined,
        isFirstSelection: true,
        timeSinceLastSelection: undefined,
      };

    case "UPDATE_LANGUAGE_SELECTION":
      return {
        ...state,
        isFirstSelection: action.payload.isFirstSelection,
        timeSinceLastSelection: action.payload.isFirstSelection
          ? undefined
          : state.timeSinceLastSelection,
      };

    default:
      return state;
  }
};

interface TranslatorControlsContextValues {
  isTranslateMode: boolean;
  setIsTranslateMode: (isTranslateMode: boolean) => void;
  activeTranslatedItem: string | undefined;
  setActiveTranslatedItem: (activeTranslatedItem: string | undefined) => void;
  isFirstSelection: boolean;
  setIsFirstSelection: (isFirstSelection: boolean) => void;
  timeSinceLastSelection: number | undefined;
  setTimeSinceLastSelection: (
    timeSinceLastSelection: number | undefined,
  ) => void;
  resetTranslationState: () => void;
  updateLanguageSelection: (
    language: string,
    isFirstSelection: boolean,
  ) => void;
}

const initialContext: TranslatorControlsContextValues = {
  isTranslateMode: false,
  setIsTranslateMode: (_: boolean) => undefined,
  activeTranslatedItem: undefined,
  setActiveTranslatedItem: (_: string | undefined) => undefined,
  isFirstSelection: true,
  setIsFirstSelection: (_: boolean) => undefined,
  timeSinceLastSelection: undefined,
  setTimeSinceLastSelection: (_: number | undefined) => undefined,
  resetTranslationState: () => undefined,
  updateLanguageSelection: (_: string, __: boolean) => undefined,
};

const TranslatorControlsContext = createContext(initialContext);

export const TranslatorControlsContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [state, dispatch] = useReducer(translatorReducer, getInitialState());
  const lastSelectionTimeRef = useRef<number | undefined>(undefined);
  const { isReadAloudModeActive } = useReadAloudMode();
  const { data: user } = useGetUser();

  // Check if user has a preferred language to determine if it's their first selection ever
  // This serves as a fallback if session storage is not available
  useEffect(() => {
    const checkPreferredLanguage = async () => {
      if (user) {
        try {
          const preferredLanguage = await getLanguagePreference(user);
          // If user has a preferred language with a code, it's not their first selection ever
          const hasSelectedBefore = !!preferredLanguage?.code;

          // Only update if session storage doesn't have a value and user has selected before
          // This ensures session storage takes precedence for session-specific tracking
          if (hasSelectedBefore) {
            try {
              const sessionStored = sessionStorage.getItem(
                FIRST_SELECTION_SESSION_KEY,
              );
              if (sessionStored === null) {
                // No session storage value, so this is not their first selection in this session
                sessionStorage.setItem(FIRST_SELECTION_SESSION_KEY, "false");
                if (state.isFirstSelection) {
                  dispatch({
                    type: "SET_FIRST_SELECTION",
                    payload: false,
                  });
                }
              }
            } catch (error) {
              console.warn("Failed to write to sessionStorage:", error);
            }
          }
        } catch (error) {
          console.warn(
            "Failed to get preferred language, keeping current first selection state:",
            error,
          );
        }
      }
    };

    checkPreferredLanguage();
  }, [user, state.isFirstSelection]);

  // Reset translation state when translate mode is turned off
  useEffect(() => {
    if (!state.isTranslateMode) {
      dispatch({ type: "RESET_TRANSLATION_STATE" });
      lastSelectionTimeRef.current = undefined;
    }
  }, [state.isTranslateMode]);

  // Turn off translate mode when read aloud mode is active
  useEffect(() => {
    if (isReadAloudModeActive) {
      dispatch({ type: "SET_TRANSLATE_MODE", payload: false });
    }
  }, [isReadAloudModeActive]);

  // Action creators
  const setIsTranslateMode = (isTranslateMode: boolean) => {
    dispatch({ type: "SET_TRANSLATE_MODE", payload: isTranslateMode });
  };

  const setActiveTranslatedItem = (
    activeTranslatedItem: string | undefined,
  ) => {
    dispatch({
      type: "SET_ACTIVE_TRANSLATED_ITEM",
      payload: activeTranslatedItem,
    });
  };

  const setIsFirstSelection = (isFirstSelection: boolean) => {
    dispatch({ type: "SET_FIRST_SELECTION", payload: isFirstSelection });
  };

  const setTimeSinceLastSelection = (
    timeSinceLastSelection: number | undefined,
  ) => {
    dispatch({
      type: "SET_TIME_SINCE_LAST_SELECTION",
      payload: timeSinceLastSelection,
    });
  };

  const resetTranslationState = () => {
    dispatch({ type: "RESET_TRANSLATION_STATE" });
  };

  const updateLanguageSelection = (
    language: string,
    isFirstSelection: boolean,
  ) => {
    // Update session storage when language is selected
    try {
      sessionStorage.setItem(FIRST_SELECTION_SESSION_KEY, "false");
    } catch (error) {
      console.warn("Failed to update sessionStorage:", error);
    }

    dispatch({
      type: "UPDATE_LANGUAGE_SELECTION",
      payload: { language, isFirstSelection },
    });
  };

  return (
    <TranslatorControlsContext.Provider
      value={{
        isTranslateMode: state.isTranslateMode,
        setIsTranslateMode,
        activeTranslatedItem: state.activeTranslatedItem,
        setActiveTranslatedItem,
        isFirstSelection: state.isFirstSelection,
        setIsFirstSelection,
        timeSinceLastSelection: state.timeSinceLastSelection,
        setTimeSinceLastSelection,
        resetTranslationState,
        updateLanguageSelection,
      }}
    >
      {children}
    </TranslatorControlsContext.Provider>
  );
};

const useTranslatorControlsContext = () => {
  const translatorControlsContext = useContext(TranslatorControlsContext);

  if (translatorControlsContext === undefined) {
    throw new Error(
      "TranslatorControlsContext must be used within a TranslatorControlsContextProvider.",
    );
  }

  return translatorControlsContext;
};

export default useTranslatorControlsContext;
